import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { TbFileSpreadsheet, TbFileTypeCsv } from 'react-icons/tb';
import { PiMagnifyingGlassDuotone } from 'react-icons/pi';
import { format } from 'date-fns';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getSummaryResolution } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';

export default function OptimizationPipelineSidebar(props) {
    const { open, title, icon: Icon, headerRight, onClose, children, className, width = '50vw', minWidth = 320, maxWidth = 720, top = 0, right = 0, bottom = 0 } = props;
    const { ts, optimizationPipelineData } = useOptimizationPipeline();
    const [rendered, setRendered] = useState(open);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let timeout = null;

        if (open) {
            setRendered(true);
            timeout = setTimeout(() => { setVisible(true); }, 10);
        } else if (rendered) {
            setVisible(false);
        }

        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [open, rendered]);

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, onClose]);

    function handleTransitionEnd(event) {
        if (event.target !== event.currentTarget) return;
        if (open) return;
        setRendered(false);
    }

    if (!rendered) return null;

    return (
        <>
            <div className={'absolute inset-0 z-40 bg-black bg-opacity-20 transition-opacity duration-200 ' + (visible ? 'opacity-100' : 'opacity-0')} onClick={onClose} />

            <aside
                className={'absolute z-50 overflow-hidden border-l border-gray-200 bg-white shadow-xl transform transition-transform duration-200 ease-in-out ' + (visible ? 'translate-x-0' : 'translate-x-full') + (className ? ' ' + className : '')}
                style={{ top, right, bottom, width, minWidth, maxWidth }}
                onTransitionEnd={handleTransitionEnd}
            >
                <div className='w-full h-full min-h-0 flex flex-col'>

                    <div className='w-full shrink-0 flex items-center justify-between gap-12 px-16 py-12 bg-gray-200 shadow-inner'>
                        <div className='min-w-0 flex-1 flex flex-col gap-3'>
                            <div className='flex items-center gap-8'>
                                {Icon && <div className='shrink-0 text-18 text-gray-700'><Icon /></div>}
                                <div className='min-w-0 text-sm font-semibold truncate'>{title}</div>
                            </div>
                            {ts && <div className='min-w-0 text-10 truncate opacity-75'>Detailed data for {format(new Date(ts), getSummaryResolution(optimizationPipelineData?.summaryDurationUnit) === 'day' ? 'yyyy.MM.dd.' : 'yyyy.MM.dd. HH:mm')}</div>}
                        </div>

                        <div className='shrink-0 flex items-center gap-8'>
                            {headerRight}
                            <button type='button' className='w-32 h-32 shrink-0 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200' onClick={onClose}>
                                <MdClose className='text-18' />
                            </button>
                        </div>
                    </div>

                    <div className='w-full h-full min-h-0 flex-1 overflow-hidden'>{children}</div>

                </div>
            </aside>
        </>
    );
}

export function SidebarHeaderButton(props) {
    const { label, icon: Icon, onClick, disabled = false, title, className = '' } = props;

    return (
        <button
            type='button'
            title={title}
            disabled={disabled}
            className={'flex items-center gap-4 h-28 px-12 rounded-lg border text-11 font-medium transition-colors duration-200 bg-white text-gray-700 border-gray-300 hover:bg-gray-100 ' + (disabled ? 'opacity-40 cursor-not-allowed hover:bg-white' : '') + (className ? ' ' + className : '')}
            onClick={onClick}
        >
            {Icon && <Icon className='text-14 shrink-0' />}
            <span>{label}</span>
        </button>
    );
}

export function SidebarExportButtons(props) {
    const { onExcel, onCsv, disabled = false } = props;

    return (
        <>
            <SidebarHeaderButton label='Excel' icon={TbFileSpreadsheet} disabled={disabled} onClick={onExcel} />
            <SidebarHeaderButton label='CSV' icon={TbFileTypeCsv} disabled={disabled} onClick={onCsv} />
        </>
    );
}

export function SidebarHeaderSearch(props) {
    const {
        value = '',
        onChange,
        placeholder = 'Search...',
        disabled = false,
        showClear = true,
        className = '',
        inputClassName = '',
        clearTitle = 'Clear search'
    } = props;

    const stringValue = value == null ? '' : String(value);
    const hasValue = stringValue.length > 0;

    function handleChange(event) {
        if (typeof onChange === 'function') {
            onChange(event.target.value);
        }
    }

    function handleClear() {
        if (typeof onChange === 'function') {
            onChange('');
        }
    }

    return (
        <div className={'flex items-center gap-6 ' + className}>

            <div className='relative'>

                <PiMagnifyingGlassDuotone className='absolute left-8 top-1/2 -translate-y-1/2 transform text-14 text-gray-400 pointer-events-none' />

                <input
                    type='text'
                    className={'w-180 h-28 border border-gray-300 bg-white rounded-lg pl-28 pr-28 text-11 leading-none outline-none transition-colors duration-200 focus:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed ' + inputClassName}
                    placeholder={placeholder}
                    value={stringValue}
                    disabled={disabled}
                    onChange={handleChange}
                />

                {showClear && hasValue ? (
                    <button
                        type='button'
                        title={clearTitle}
                        disabled={disabled}
                        className='absolute right-4 top-1/2 -translate-y-1/2 transform w-20 h-20 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed'
                        onClick={handleClear}
                    >
                        <MdClose className='text-13' />
                    </button>
                ) : null}
            </div>
        </div>
    );
}

export function SidebarHeaderCheckbox(props) {
    const {
        id,
        label,
        checked = false,
        onChange,
        disabled = false,
        className = '',
        inputClassName = ''
    } = props;

    function handleChange(event) {
        if (typeof onChange === 'function') {
            onChange(event.target.checked);
        }
    }

    return (
        <label htmlFor={id} className={'flex items-center gap-6 text-11 text-gray-600 whitespace-nowrap ' + className}>

            <input
                id={id}
                type='checkbox'
                checked={checked}
                disabled={disabled}
                className={inputClassName}
                onChange={handleChange}
            />
            <span>{label}</span>
        </label>
    );
}